import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import {
  Package, Plus, Search, Link2, Unlink, RefreshCw,
  Loader2, X, DollarSign,
  Box, ExternalLink, Edit2, AlertTriangle, ArrowDownUp, Trash2
} from 'lucide-react';
import { ConvertToInternalModal } from '../../components/stock/ConvertToInternalModal';
import { Can, useCan } from '../../lib/rbac';

// ── Types ──

interface Stats {
  total_products: number;
  in_stock: number;
  out_of_stock: number;
  total_qty: number;
  total_value: number;
  linked_count: number;
}

interface MLSite {
  site_id: string;
  site_item_id: string;
  status: string;
  permalink: string | null;
}

interface Product {
  id: number;
  internal_sku: string;
  title: string;
  sku: string;  // alias for internal_sku
  name: string; // alias for title
  asin: string | null;
  image_url: string | null;
  qty_on_hand: number;
  qty_available: number;
  qty_allocated: number;
  qty_incoming: number;
  unit_cost: number;
  supplier_id: number | null;
  supplier_name: string | null;
  linked_item_id: string | null;
  ml_title: string | null;
  ml_status: string | null;
  ml_price: number | null;
  ml_stock: number | null;
  ml_sites: MLSite[] | null;
  notes: string | null;
  source_type: string;
  created_at: string;
  updated_at: string;
}

function mapProduct(raw: any): Product {
  return { ...raw, sku: raw.internal_sku || raw.asin || '', name: raw.title || '' };
}

interface Movement {
  id: number;
  movement_type: string;
  qty: number;
  balance_after: number;
  reference_type: string | null;
  reference_id: string | null;
  notes: string | null;
  created_at: string;
}

interface LinkedItem {
  item_id: string;
  seller_id: number;
  title: string | null;
  status: string | null;
}

interface ProductDetail extends Product {
  movements: Movement[];
  linked_items: LinkedItem[];
}

interface Suggestion {
  item_id: string;
  title: string;
  status: string;
  thumbnail: string | null;
  price: number | null;
  site_id: string | null;
}

interface Supplier {
  id: number;
  name: string;
}

// ── Helpers ──

const fmt = (n: number) => `$${n.toFixed(2)}`;

// Mismos flags y colores de estado que Catálogo → Item → Sites
const SITE_FLAGS: Record<string, string> = {
  MLA: '\u{1F1E6}\u{1F1F7}', MLB: '\u{1F1E7}\u{1F1F7}', MLM: '\u{1F1F2}\u{1F1FD}',
  MLC: '\u{1F1E8}\u{1F1F1}', MCO: '\u{1F1E8}\u{1F1F4}', MLU: '\u{1F1FA}\u{1F1FE}',
};

const SITE_STATUS_LABELS: Record<string, string> = {
  active: 'activo',
  paused: 'pausado',
  closed: 'cerrado',
  deleted: 'eliminado',
  under_review: 'en revisión',
};

const SITE_STATUS_CLASSES: Record<string, string> = {
  active: 'text-green-600 dark:text-green-400 border-green-300 dark:border-green-800',
  paused: 'text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-800',
  closed: 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-800',
  deleted: 'text-red-600 dark:text-red-400 border-red-300 dark:border-red-800',
  under_review: 'text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-800',
};

const MOVEMENT_LABELS: Record<string, string> = {
  purchase: 'Compra',
  sale: 'Venta',
  adjustment: 'Ajuste',
  return: 'Devolución',
  transfer: 'Transferencia',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// ── Component ──

export default function InternalStockTab({ sellerId }: { sellerId: number }) {
  // ── Permisos ──
  const can = useCan();
  const canMoney = can('global.view_money');
  const canOperate = can('purchases.operate_internal_stock');
  const canReprice = can('purchases.reprice_ml');

  // ── State ──
  const [products, setProducts] = useState<Product[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const pag = usePagination('internal-stock');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [stockFilter, setStockFilter] = useState<'' | 'in_stock' | 'out_of_stock'>('');
  const [linkedFilter, setLinkedFilter] = useState<'' | 'true' | 'false'>('');
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // Smart create form
  const [showCreate, setShowCreate] = useState(false);
  const [smartSearch, setSmartSearch] = useState('');
  const [smartResults, setSmartResults] = useState<any[]>([]);
  const [smartLoading, setSmartLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null); // existing product to adopt
  const [isNewProduct, setIsNewProduct] = useState(false); // creating brand new
  const [createForm, setCreateForm] = useState({ sku: '', name: '', qty: '', unit_cost: '', asin: '', supplier_id: '', notes: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Detail panel
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [editCostValue, setEditCostValue] = useState('');
  const [editingQty, setEditingQty] = useState(false);
  const [editQtyValue, setEditQtyValue] = useState('');
  const [repriceMsg, setRepriceMsg] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);

  // Link flow
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [linkingItemId, setLinkingItemId] = useState<string | null>(null);

  // Action loading
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  // Rescatables section
  const [rescatables, setRescatables] = useState<Array<{
    product_id: number; asin: string; title: string;
    qty_on_hand: number; last_purchase_cost: number | null; avg_cost: number | null;
    image_url: string | null;
  }>>([]);
  const [showRescatables, setShowRescatables] = useState(false);
  const [convertTarget, setConvertTarget] = useState<{
    stockProductId: number; productTitle: string; productAsin: string | null;
    qtyOnHand: number; defaultQty: number; defaultUnitCost: number;
  } | null>(null);

  // ── Data fetching ──

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pag.page),
        per_page: String(pag.perPage),
        seller_id: String(sellerId),
      });
      if (searchTerm) params.set('search', searchTerm);
      if (stockFilter) params.set('stock_filter', stockFilter);
      if (linkedFilter) params.set('linked', linkedFilter);

      const res = await api.get<any>(`/internal-stock/products?${params}`);
      if (res.success) {
        const raw = Array.isArray(res.data) ? res.data : [];
        setProducts(raw.map(mapProduct));
        pag.setTotal((res as any).total ?? 0);
      }
    } finally {
      setLoading(false);
    }
  }, [pag.page, pag.perPage, sellerId, searchTerm, stockFilter, linkedFilter]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get<Stats>(`/internal-stock/stats?seller_id=${sellerId}`);
      if (res.success && res.data) setStats(res.data);
    } finally {
      setStatsLoading(false);
    }
  }, [sellerId]);

  const fetchSuppliers = useCallback(async () => {
    const res = await api.get<Supplier[]>('/suppliers');
    if (res.success && res.data) setSuppliers(res.data);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const fetchRescatables = useCallback(async () => {
    const res = await api.get<any[]>(`/internal-stock/rescatables?seller_id=${sellerId}`);
    if (res.success && res.data) setRescatables(res.data);
  }, [sellerId]);

  useEffect(() => { fetchRescatables(); }, [fetchRescatables]);

  // ── Search with debounce ──

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput);
      pag.resetPage();
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ── Reprice feedback ──

  const describeReprice = (r: { error?: string; repriced?: number; linked?: number; sites_ok?: number; sites_failed?: number; skipped?: number; errors?: string[] } | null | undefined): { kind: 'ok' | 'warn' | 'error'; text: string } => {
    if (!r) return { kind: 'ok', text: 'Costo guardado. El precio no se recalculó porque el costo no cambió.' };
    if (r.error) return { kind: 'error', text: `Costo guardado, pero el re-precio falló: ${r.error}` };
    if (!r.linked) return { kind: 'warn', text: 'Costo guardado, pero este producto no tiene publicación vinculada: no se cambió ningún precio en Mercado Libre.' };
    const parts: string[] = [];
    if (r.repriced) parts.push(`Precio actualizado en ${r.repriced} publicación(es) y ${r.sites_ok ?? 0} país(es) de Mercado Libre`);
    if (r.skipped) parts.push(`${r.skipped} publicación(es) sin datos para calcular precio`);
    if (r.sites_failed) parts.push(`${r.sites_failed} país(es) rechazaron el cambio`);
    if (r.errors?.length) parts.push(`Errores: ${r.errors.join('; ')}`);
    if (!parts.length) return { kind: 'warn', text: 'Costo guardado, pero no se re-preció ninguna publicación.' };
    const kind: 'ok' | 'warn' = r.sites_failed || r.errors?.length || !r.repriced ? 'warn' : 'ok';
    return { kind, text: parts.join(' · ') };
  };

  // ── Actions ──

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await api.get<any>(`/internal-stock/products/${id}?seller_id=${sellerId}`);
      if (res.success && res.data) {
        setDetail({ ...mapProduct(res.data), movements: res.data.movements || [], linked_items: res.data.linked_items || [] } as ProductDetail);
        setEditingCost(false);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  // Smart search with debounce
  useEffect(() => {
    if (!smartSearch || smartSearch.length < 2) { setSmartResults([]); return; }
    const t = setTimeout(async () => {
      setSmartLoading(true);
      try {
        const res = await api.get<any[]>(`/internal-stock/search?q=${encodeURIComponent(smartSearch)}&seller_id=${sellerId}`);
        if (res.success && res.data) setSmartResults(res.data);
      } finally {
        setSmartLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [smartSearch, sellerId]);

  const resetCreateForm = () => {
    setShowCreate(false);
    setSmartSearch('');
    setSmartResults([]);
    setSelectedProduct(null);
    setIsNewProduct(false);
    setCreateForm({ sku: '', name: '', qty: '', unit_cost: '', asin: '', supplier_id: '', notes: '' });
    setCreateError('');
  };

  const selectExistingProduct = (p: any) => {
    setSmartResults([]);
    setSmartSearch('');
    // Sin la llave del dinero el costo guardado NO se precarga en el campo visible.
    const cost = !canMoney ? '' : p.unit_cost ? String(p.unit_cost) : p.last_amazon_cost ? String(p.last_amazon_cost) : '';
    if (p.product_id) {
      // Existing stock_products entry — adopt flow
      setSelectedProduct(p);
      setIsNewProduct(false);
      setCreateForm(prev => ({ ...prev, qty: '', unit_cost: cost }));
    } else {
      // ML-only product — pre-fill as new product with ML data
      setSelectedProduct(p);
      setIsNewProduct(true);
      setCreateForm(prev => ({
        ...prev,
        sku: p.asin || p.internal_sku || '',
        name: p.title || '',
        asin: p.asin || '',
        qty: '',
        unit_cost: cost,
        notes: '',
      }));
    }
  };

  const startNewProduct = () => {
    setIsNewProduct(true);
    setSelectedProduct(null);
    setSmartResults([]);
    setCreateForm(prev => ({ ...prev, name: smartSearch, sku: '', qty: '', unit_cost: '', asin: '', supplier_id: '', notes: '' }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      if (selectedProduct?.product_id) {
        // Adopt existing product
        if (!createForm.qty || !createForm.unit_cost) {
          setCreateError('Cantidad y Costo son requeridos');
          setCreating(false);
          return;
        }
        const res = await api.post<any>('/internal-stock/adopt', {
          product_id: selectedProduct.product_id,
          internal_sku: createForm.sku || null,
          qty: parseInt(createForm.qty),
          unit_cost: parseFloat(createForm.unit_cost),
          supplier_id: createForm.supplier_id ? parseInt(createForm.supplier_id) : null,
          notes: createForm.notes || null,
          seller_id: sellerId,
        });
        if (res.success) {
          resetCreateForm();
          fetchProducts();
          fetchStats();
          if ((res as any).suggestions?.length > 0 && (res as any).data?.id) {
            openDetail(res.data.id);
          }
        } else {
          setCreateError(res.error || 'Error al adoptar producto');
        }
      } else {
        // Create new product
        if (!createForm.name.trim() || !createForm.qty || !createForm.unit_cost) {
          setCreateError('Nombre, Cantidad y Costo son requeridos');
          setCreating(false);
          return;
        }
        const body: Record<string, unknown> = {
          sku: createForm.sku.trim() || null,
          name: createForm.name.trim(),
          qty: parseInt(createForm.qty),
          unit_cost: parseFloat(createForm.unit_cost),
          seller_id: sellerId,
        };
        if (createForm.asin.trim()) body.asin = createForm.asin.trim();
        if (createForm.supplier_id) body.supplier_id = parseInt(createForm.supplier_id);
        if (createForm.notes.trim()) body.notes = createForm.notes.trim();
        if (selectedProduct?.image_url) body.image_url = selectedProduct.image_url;

        const res = await api.post<any>('/internal-stock/products', body);
        if (res.success) {
          resetCreateForm();
          fetchProducts();
          fetchStats();
        } else {
          setCreateError(res.error || 'Error al crear producto');
        }
      }
    } finally {
      setCreating(false);
    }
  };

  // Editar cantidad: fija qty_internal absoluto + sync inmediato a ML.
  // En 0 el backend dispara el circuito de agotamiento: vuelve al arbitraje
  // Amazon si el producto tiene ASIN, o queda qty=0 en ML (se pausa solo).
  const handleUpdateQty = async () => {
    if (!detail) return;
    const qty = parseInt(editQtyValue, 10);
    if (isNaN(qty) || qty < 0) return;
    setActionLoading(prev => ({ ...prev, updateQty: true }));
    try {
      const res = await api.post(`/internal-stock/products/${detail.id}/adjust-qty`, {
        qty, seller_id: sellerId,
      }) as {
        success: boolean; error?: string;
        data?: { qty_internal: number; delta: number; sync?: { synced?: number; deferred_switch?: boolean; qty_available?: number } };
      };
      if (res.success && res.data) {
        setEditingQty(false);
        const sync = res.data.sync || {};
        if (sync.deferred_switch) {
          setRepriceMsg({ kind: 'warn', text: 'Cantidad en 0 — el aviso vuelve al arbitraje de Amazon (precio restaurado y desvinculado en unos segundos).' });
        } else if (qty === 0) {
          setRepriceMsg({ kind: 'warn', text: 'Cantidad en 0 — el aviso queda sin stock en ML (se pausa solo, no tiene ASIN de Amazon para arbitraje).' });
        } else {
          setRepriceMsg({ kind: 'ok', text: `Cantidad actualizada a ${qty} y sincronizada con ML (${sync.synced ?? 0} aviso/s).` });
        }
        fetchProducts();
        fetchStats();
        openDetail(detail.id);
      } else {
        setRepriceMsg({ kind: 'error', text: res.error || 'No se pudo actualizar la cantidad' });
      }
    } finally {
      setActionLoading(prev => ({ ...prev, updateQty: false }));
    }
  };

  const handleUpdateCost = async () => {
    if (!detail) return;
    const cost = parseFloat(editCostValue);
    if (isNaN(cost) || cost < 0) return;
    setActionLoading(prev => ({ ...prev, updateCost: true }));
    try {
      const res = await api.put(`/internal-stock/products/${detail.id}`, { unit_cost: cost, seller_id: sellerId }) as {
        success: boolean; error?: string;
        reprice?: { error?: string; repriced?: number; linked?: number; sites_ok?: number; sites_failed?: number; skipped?: number; errors?: string[] } | null;
      };
      if (res.success) {
        setDetail(prev => prev ? { ...prev, unit_cost: cost } : prev);
        setEditingCost(false);
        setRepriceMsg(describeReprice(res.reprice));
        fetchProducts();
        fetchStats();
        openDetail(detail.id);
      } else {
        setRepriceMsg({ kind: 'error', text: res.error || 'No se pudo guardar el costo' });
      }
    } finally {
      setActionLoading(prev => ({ ...prev, updateCost: false }));
    }
  };

  const loadSuggestions = async (productId: number) => {
    setSuggestionsLoading(true);
    setShowSuggestions(true);
    setSuggestions([]);
    try {
      const res = await api.get<Suggestion[]>(`/internal-stock/products/${productId}/suggest?seller_id=${sellerId}`);
      if (res.success && res.data) setSuggestions(res.data);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleLink = async (productId: number, itemId: string) => {
    setLinkingItemId(itemId);
    try {
      const res = await api.post(`/internal-stock/products/${productId}/link`, { seller_id: sellerId, item_id: itemId });
      if (res.success) {
        setShowSuggestions(false);
        openDetail(productId);
        fetchProducts();
        fetchStats();
      }
    } finally {
      setLinkingItemId(null);
    }
  };

  const handleUnlink = async (productId: number, itemId: string) => {
    setActionLoading(prev => ({ ...prev, [`unlink_${itemId}`]: true }));
    try {
      const res = await api.delete(`/internal-stock/products/${productId}/link/${itemId}?seller_id=${sellerId}`);
      if (res.success) {
        openDetail(productId);
        fetchProducts();
        fetchStats();
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [`unlink_${itemId}`]: false }));
    }
  };

  const handleReprice = async (productId: number) => {
    setActionLoading(prev => ({ ...prev, [`reprice_${productId}`]: true }));
    try {
      const res = await api.post<{ repriced?: number; linked?: number; sites_ok?: number; sites_failed?: number; skipped?: number; errors?: string[] }>(`/internal-stock/products/${productId}/reprice`, { seller_id: sellerId });
      if (res.success) {
        setRepriceMsg(describeReprice(res.data ?? null));
        if (detail?.id === productId) openDetail(productId);
      } else {
        setRepriceMsg({ kind: 'error', text: res.error || 'No se pudo recalcular el precio' });
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [`reprice_${productId}`]: false }));
    }
  };

  const handleSyncML = async (productId: number) => {
    setActionLoading(prev => ({ ...prev, [`sync_${productId}`]: true }));
    try {
      await api.post(`/internal-stock/products/${productId}/sync-ml`, { seller_id: sellerId });
    } finally {
      setActionLoading(prev => ({ ...prev, [`sync_${productId}`]: false }));
    }
  };

  // ── Render ──

  return (
    <div className="space-y-4">
      {/* Rescatables — productos source_type=amazon con qty_on_hand > 0 (huérfanos de arbitraje) */}
      {rescatables.length > 0 && (
        <div className="rounded-lg border border-cyan-200 bg-cyan-50 dark:bg-cyan-900/10 dark:border-cyan-800">
          <button
            onClick={() => setShowRescatables(v => !v)}
            className="w-full flex items-center justify-between p-3 text-left"
          >
            <div className="flex items-center gap-2">
              <ArrowDownUp className="w-4 h-4 text-cyan-600" />
              <span className="font-semibold text-cyan-800 dark:text-cyan-300">
                Rescatables: {rescatables.length} productos · {rescatables.reduce((s, r) => s + r.qty_on_hand, 0)} unidades
              </span>
            </div>
            <span className="text-sm text-cyan-700">{showRescatables ? 'Ocultar' : 'Ver'}</span>
          </button>
          {showRescatables && (
            <div className="p-3 pt-0 space-y-2 max-h-96 overflow-y-auto">
              {rescatables.map(r => (
                <div key={r.product_id} className="flex items-center gap-3 p-2 bg-white dark:bg-gray-800 rounded border border-cyan-100 dark:border-cyan-800">
                  {r.image_url && <img src={r.image_url} alt="" className="w-10 h-10 object-cover rounded" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    <div className="text-xs text-gray-500 flex gap-3">
                      <span>ASIN: {r.asin}</span>
                      <span>Stock: {r.qty_on_hand}</span>
                      {r.last_purchase_cost && canMoney && <span>Último costo: ${r.last_purchase_cost.toFixed(2)}</span>}
                    </div>
                  </div>
                  <Can cap="purchases.operate_internal_stock">
                    <button
                      onClick={() => setConvertTarget({
                        stockProductId: r.product_id,
                        productTitle: r.title,
                        productAsin: r.asin,
                        qtyOnHand: r.qty_on_hand,
                        defaultQty: r.qty_on_hand,
                        defaultUnitCost: r.last_purchase_cost ?? r.avg_cost ?? 0,
                      })}
                      className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      <ArrowDownUp className="w-3 h-3" /> Convertir
                    </button>
                    <button
                      onClick={async () => {
                        const confirmed = window.confirm(
                          `¿Eliminar de la lista de rescatables?\n\n` +
                          `Producto: ${r.title}\n` +
                          `ASIN: ${r.asin}\n` +
                          `Unidades a zerar: ${r.qty_on_hand}\n\n` +
                          `Esto zera el stock fantasma (qty_on_hand → 0) y deja audit trail. ` +
                          `Solo confirmá si las unidades NO existen físicamente.`
                        );
                        if (!confirmed) return;
                        setActionLoading(prev => ({ ...prev, [`dismiss_${r.product_id}`]: true }));
                        try {
                          const res = await api.post<any>(
                            `/internal-stock/rescatables/${r.product_id}/dismiss`,
                            { seller_id: sellerId }
                          );
                          if (res.success) {
                            fetchRescatables();
                            fetchStats();
                          } else {
                            window.alert(`Error: ${res.error || 'no se pudo eliminar'}`);
                          }
                        } finally {
                          setActionLoading(prev => ({ ...prev, [`dismiss_${r.product_id}`]: false }));
                        }
                      }}
                      disabled={!!actionLoading[`dismiss_${r.product_id}`]}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-[var(--color-border)] text-[var(--color-error)] hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
                      title="Zerar stock fantasma y quitar de la lista"
                    >
                      {actionLoading[`dismiss_${r.product_id}`] ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                      Eliminar
                    </button>
                  </Can>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total Productos', value: stats?.total_products?.toLocaleString() ?? '-', icon: Package, color: 'text-[var(--color-primary)]' },
          { label: 'En Stock', value: stats?.in_stock?.toLocaleString() ?? '-', icon: Box, color: 'text-[var(--color-success)]' },
          { label: 'Sin Stock', value: stats?.out_of_stock?.toLocaleString() ?? '-', icon: AlertTriangle, color: 'text-[var(--color-error)]' },
          ...(canMoney ? [{ label: 'Valor Total', value: stats ? fmt(stats.total_value) : '-', icon: DollarSign, color: 'text-[var(--color-warning)]' }] : []),
          { label: 'Vinculados ML', value: stats?.linked_count?.toLocaleString() ?? '-', icon: Link2, color: 'text-[var(--color-primary)]' },
        ].map((s, i) => (
          <div key={i} className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3">
            <div className="flex items-center gap-2 mb-1">
              <s.icon size={16} className={s.color} />
              <span className="text-xs text-[var(--color-text-secondary)]">{s.label}</span>
            </div>
            <div className={`text-lg font-bold ${statsLoading ? 'animate-pulse' : ''} text-[var(--color-text)]`}>
              {statsLoading ? '...' : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar por SKU, nombre o ASIN..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] placeholder:text-[var(--color-text-secondary)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
          />
        </div>

        {/* Stock filter */}
        <select
          value={stockFilter}
          onChange={e => { setStockFilter(e.target.value as any); pag.resetPage(); }}
          className="text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        >
          <option value="">Todos</option>
          <option value="in_stock">En Stock</option>
          <option value="out_of_stock">Sin Stock</option>
        </select>

        {/* Linked filter */}
        <select
          value={linkedFilter}
          onChange={e => { setLinkedFilter(e.target.value as any); pag.resetPage(); }}
          className="text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        >
          <option value="">ML: Todos</option>
          <option value="true">Vinculados</option>
          <option value="false">No Vinculados</option>
        </select>

        <div className="flex-1" />

        {/* Refresh */}
        <button
          onClick={() => { fetchProducts(); fetchStats(); }}
          className="p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-secondary)] transition-colors"
          title="Refrescar"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>

        {/* Create */}
        <Can cap="purchases.operate_internal_stock">
          <button
            onClick={() => { setShowCreate(true); setCreateError(''); }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
          >
            <Plus size={16} />
            Cargar Producto
          </button>
        </Can>
      </div>

      {/* Smart Create Form */}
      {showCreate && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-[var(--color-text)]">
              {selectedProduct ? 'Agregar Stock a Producto Existente' : isNewProduct ? 'Crear Producto Nuevo' : 'Cargar Stock Interno'}
            </h3>
            <button onClick={resetCreateForm} className="p-1 rounded hover:bg-[var(--color-bg)]">
              <X size={16} className="text-[var(--color-text-secondary)]" />
            </button>
          </div>

          {/* Step 1: Smart search (shown when no product selected and not creating new) */}
          {!selectedProduct && !isNewProduct && (
            <div className="relative">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" />
                <input
                  type="text"
                  value={smartSearch}
                  onChange={e => setSmartSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                  placeholder="Buscar por ASIN, nombre, SKU o UPC..."
                  autoFocus
                />
                {smartLoading && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--color-text-secondary)]" />}
              </div>

              {/* Dropdown results */}
              {smartResults.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  {smartResults.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => selectExistingProduct(r)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[var(--color-bg-secondary)] text-left border-b border-[var(--color-border)] last:border-0 transition-colors"
                    >
                      {r.image_url ? (
                        <img src={r.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-[var(--color-bg-secondary)] flex items-center justify-center flex-shrink-0">
                          <Package size={16} className="text-[var(--color-text-secondary)]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[var(--color-text)]">{r.title}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {r.asin && <span className="text-xs font-mono text-[var(--color-text-secondary)]">{r.asin}</span>}
                          {r.upc && <span className="text-xs font-mono text-[var(--color-text-secondary)]">UPC: {r.upc}</span>}
                          {r.internal_sku && <span className="text-xs font-mono text-cyan-500">SKU: {r.internal_sku}</span>}
                          {r.has_ml_listing && <span className="text-xs text-green-500">ML: {r.ml_item_id}</span>}
                          {r.in_stock_db && <span className="text-xs text-blue-500">En DB</span>}
                        </div>
                        {r.amazon_title && (
                          <p className="text-xs text-[var(--color-text-secondary)] italic mt-0.5 truncate" title={r.amazon_title}>
                            ↪ {r.amazon_title}
                          </p>
                        )}
                      </div>
                      {r.last_amazon_cost && canMoney && (
                        <span className="text-xs text-[var(--color-text-secondary)] flex-shrink-0">${parseFloat(r.last_amazon_cost).toFixed(2)}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* No results + create new button */}
              {smartSearch.length >= 2 && !smartLoading && smartResults.length === 0 && (
                <div className="mt-3 p-3 bg-[var(--color-bg)] rounded border border-dashed border-[var(--color-border)] text-center">
                  <p className="text-sm text-[var(--color-text-secondary)] mb-2">No se encontraron productos</p>
                  <button
                    onClick={startNewProduct}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
                  >
                    <Plus size={14} />
                    Crear producto nuevo
                  </button>
                </div>
              )}

              {smartSearch.length >= 2 && !smartLoading && smartResults.length > 0 && (
                <div className="mt-2 text-center">
                  <button
                    onClick={startNewProduct}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    No es ninguno de estos? Crear producto nuevo
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 2a: Selected existing product — show preview + qty/cost fields */}
          {selectedProduct && !isNewProduct && (
            <form onSubmit={handleCreate}>
              <div className="flex items-center gap-3 p-3 mb-3 bg-[var(--color-bg)] rounded-lg border border-[var(--color-border)]">
                {selectedProduct.image_url ? (
                  <img src={selectedProduct.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded bg-[var(--color-bg-secondary)] flex items-center justify-center">
                    <Package size={20} className="text-[var(--color-text-secondary)]" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="text-sm font-medium text-[var(--color-text)]">{selectedProduct.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {selectedProduct.asin && <span className="text-xs font-mono text-[var(--color-text-secondary)]">ASIN: {selectedProduct.asin}</span>}
                    {selectedProduct.has_ml_listing && <span className="text-xs text-green-500">Publicado en ML</span>}
                  </div>
                </div>
                <button type="button" onClick={() => { setSelectedProduct(null); setSmartSearch(''); }} className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]">
                  Cambiar
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Cantidad *</label>
                  <input type="number" min="1" value={createForm.qty} onChange={e => setCreateForm(p => ({ ...p, qty: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    placeholder="0" required autoFocus />
                </div>
                <div>
                  <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Costo Unitario (USD) *</label>
                  <input type="number" min="0" step="0.01" value={createForm.unit_cost} onChange={e => setCreateForm(p => ({ ...p, unit_cost: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    placeholder="0.00" required />
                </div>
                <div>
                  <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Proveedor</label>
                  <select value={createForm.supplier_id} onChange={e => setCreateForm(p => ({ ...p, supplier_id: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]">
                    <option value="">Sin proveedor</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[var(--color-text-secondary)] mb-1">SKU Interno (opcional)</label>
                  <input type="text" value={createForm.sku} onChange={e => setCreateForm(p => ({ ...p, sku: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    placeholder="SKU-001" />
                </div>
                <div>
                  <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Notas</label>
                  <input type="text" value={createForm.notes} onChange={e => setCreateForm(p => ({ ...p, notes: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                    placeholder="Notas opcionales" />
                </div>
                <div className="flex items-end gap-2">
                  <button type="submit" disabled={creating}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors">
                    {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                    Agregar Stock
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Step 2b: New product — full form */}
          {isNewProduct && (
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-[var(--color-text-secondary)] mb-1">SKU *</label>
                <input type="text" value={createForm.sku} onChange={e => setCreateForm(p => ({ ...p, sku: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  placeholder="SKU-001" required autoFocus />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Nombre *</label>
                <input type="text" value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  placeholder="Nombre del producto" required />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Cantidad *</label>
                <input type="number" min="0" value={createForm.qty} onChange={e => setCreateForm(p => ({ ...p, qty: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  placeholder="0" required />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Costo Unitario (USD) *</label>
                <input type="number" min="0" step="0.01" value={createForm.unit_cost} onChange={e => setCreateForm(p => ({ ...p, unit_cost: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  placeholder="0.00" required />
              </div>
              <div>
                <label className="block text-xs text-[var(--color-text-secondary)] mb-1">Proveedor</label>
                <select value={createForm.supplier_id} onChange={e => setCreateForm(p => ({ ...p, supplier_id: e.target.value }))}
                  className="w-full px-3 py-2 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]">
                  <option value="">Sin proveedor</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button type="submit" disabled={creating}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Crear
                </button>
                <button type="button" onClick={() => { setIsNewProduct(false); setSmartSearch(''); }}
                  className="px-4 py-2 text-sm rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] transition-colors">
                  Volver
                </button>
              </div>
            </form>
          )}

          {createError && <p className="mt-2 text-sm text-red-500">{createError}</p>}
        </div>
      )}

      {/* Product Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                {['IMG', 'SKU', 'Nombre', 'Stock', 'Disponible', 'Costo', 'Proveedor', 'ML Link', 'Acciones'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Loader2 size={24} className="animate-spin mx-auto text-[var(--color-primary)]" />
                    <p className="mt-2 text-sm text-[var(--color-text-secondary)]">Cargando productos...</p>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center">
                    <Package size={32} className="mx-auto text-[var(--color-text-secondary)] opacity-40" />
                    <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                      {searchTerm || stockFilter || linkedFilter
                        ? 'No se encontraron productos con esos filtros'
                        : 'No hay productos cargados. Usa "+ Cargar Producto" para comenzar.'}
                    </p>
                  </td>
                </tr>
              ) : (
                products.map(p => (
                  <tr
                    key={p.id}
                    onClick={() => openDetail(p.id)}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          loading="lazy"
                          className="w-10 h-10 rounded object-cover bg-[var(--color-bg)] border border-[var(--color-border)]"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-[var(--color-bg)] border border-[var(--color-border)] flex items-center justify-center">
                          <Package size={14} className="text-[var(--color-text-secondary)] opacity-40" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[var(--color-text)]">{p.sku}</td>
                    <td className="px-4 py-3 text-[var(--color-text)] max-w-[250px]">
                      <div>{p.name}</div>
                      {p.asin && <div className="text-xs text-[var(--color-text-secondary)]">{p.asin}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[var(--color-text)] font-medium">{(p.qty_on_hand ?? 0).toLocaleString()}</span>
                        <StatusBadge
                          status={p.qty_on_hand > 0 ? 'ok' : 'error'}
                          label={p.qty_on_hand > 0 ? 'EN STOCK' : 'SIN STOCK'}
                          size="sm"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-text)]">{(p.qty_available ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-[var(--color-text)] font-mono">{canMoney ? fmt(p.unit_cost) : '—'}</td>
                    <td className="px-4 py-3 text-[var(--color-text-secondary)] text-xs">{p.supplier_name || '-'}</td>
                    <td className="px-4 py-3">
                      {p.linked_item_id ? (
                        <div onClick={e => e.stopPropagation()}>
                          <Link
                            to={`/catalog/${p.linked_item_id}`}
                            title="Ver en Catálogo (pestaña Sites)"
                            className="inline-block hover:opacity-80 transition-opacity"
                          >
                            <StatusBadge status="ok" label={`Vinculado ${p.linked_item_id}`} size="sm" />
                          </Link>
                          {(p.ml_sites?.length ?? 0) > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {p.ml_sites!.map(s => (
                                <a
                                  key={s.site_item_id}
                                  href={s.permalink || `https://www.mercadolibre.com/item/${s.site_item_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`${s.site_item_id} — ${SITE_STATUS_LABELS[s.status] || s.status}`}
                                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded border bg-[var(--color-bg)] hover:opacity-80 transition-opacity ${SITE_STATUS_CLASSES[s.status] || 'text-[var(--color-text-secondary)] border-[var(--color-border)]'}`}
                                >
                                  <span>{SITE_FLAGS[s.site_id] || ''}</span>
                                  <span>{s.site_id}</span>
                                  <span className="opacity-70">{SITE_STATUS_LABELS[s.status] || s.status}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <StatusBadge status="unknown" label="No vinculado" size="sm" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        {p.linked_item_id && (
                          <>
                            {canReprice && (
                              <button
                                onClick={() => handleReprice(p.id)}
                                disabled={!!actionLoading[`reprice_${p.id}`]}
                                className="p-1.5 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors"
                                title="Recalcular precio ML"
                              >
                                {actionLoading[`reprice_${p.id}`] ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
                              </button>
                            )}
                            {canOperate && (
                              <button
                                onClick={() => handleSyncML(p.id)}
                                disabled={!!actionLoading[`sync_${p.id}`]}
                                className="p-1.5 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors"
                                title="Sincronizar stock ML"
                              >
                                {actionLoading[`sync_${p.id}`] ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                              </button>
                            )}
                          </>
                        )}
                        {!p.linked_item_id && p.asin && canOperate && (
                          <button
                            onClick={() => { openDetail(p.id); loadSuggestions(p.id); }}
                            className="p-1.5 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors"
                            title="Buscar items ML para vincular"
                          >
                            <Link2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-[var(--color-border)]">
          <Pagination
            page={pag.page}
            totalPages={pag.totalPages}
            total={pag.total}
            perPage={pag.perPage}
            onPageChange={pag.setPage}
            onPerPageChange={pag.setPerPage}
          />
        </div>
      </div>

      {/* Detail SlidePanel */}
      {(detail || detailLoading) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => { setDetail(null); setShowSuggestions(false); setRepriceMsg(null); }}>
          <div
            className="w-full max-w-xl bg-[var(--color-bg)] h-full overflow-y-auto shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-[var(--color-bg)] border-b border-[var(--color-border)] px-4 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Package size={18} className="text-[var(--color-primary)]" />
                <h3 className="font-bold text-[var(--color-text)]">
                  {detailLoading ? 'Cargando...' : detail?.sku}
                </h3>
                {detail && (
                  <StatusBadge
                    status={detail.qty_on_hand > 0 ? 'ok' : 'error'}
                    label={detail.qty_on_hand > 0 ? 'EN STOCK' : 'SIN STOCK'}
                    size="sm"
                  />
                )}
              </div>
              <button
                onClick={() => { setDetail(null); setShowSuggestions(false); setRepriceMsg(null); }}
                className="p-1 rounded hover:bg-[var(--color-bg-secondary)]"
              >
                <X size={18} className="text-[var(--color-text-secondary)]" />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={24} className="animate-spin text-[var(--color-primary)]" />
              </div>
            ) : detail && (
              <div className="p-4 space-y-6">
                {repriceMsg && (
                  <div className={`rounded-lg border px-3 py-2 text-sm ${
                    repriceMsg.kind === 'ok' ? 'border-green-300 bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800'
                    : repriceMsg.kind === 'warn' ? 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800'
                    : 'border-red-300 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800'
                  }`}>
                    {repriceMsg.text}
                  </div>
                )}
                {/* Product Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">Producto</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Nombre</span>
                      <p className="text-[var(--color-text)] font-medium">{detail.name}</p>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">SKU</span>
                      <p className="text-[var(--color-text)] font-mono">{detail.sku}</p>
                    </div>
                    {detail.asin && (
                      <div>
                        <span className="text-[var(--color-text-secondary)]">ASIN</span>
                        <p className="text-[var(--color-text)] font-mono">{detail.asin}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Proveedor</span>
                      <p className="text-[var(--color-text)]">{detail.supplier_name || '-'}</p>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Qty on Hand</span>
                      {editingQty ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={editQtyValue}
                            onChange={e => setEditQtyValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                            autoFocus
                          />
                          <button
                            onClick={handleUpdateQty}
                            disabled={!!actionLoading.updateQty}
                            className="text-xs px-2 py-1 rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
                          >
                            {actionLoading.updateQty ? <Loader2 size={12} className="animate-spin" /> : 'Guardar'}
                          </button>
                          <button
                            onClick={() => setEditingQty(false)}
                            className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)]"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <p className="text-[var(--color-text)] font-bold text-lg">{(detail.qty_on_hand ?? 0).toLocaleString()}</p>
                          <Can cap="purchases.operate_internal_stock">
                            <button
                              onClick={() => { setEditingQty(true); setEditQtyValue(String(detail.qty_on_hand ?? 0)); }}
                              className="p-0.5 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]"
                              title="Editar cantidad (sincroniza con ML al guardar; en 0 vuelve al arbitraje o se pausa)"
                            >
                              <Edit2 size={12} />
                            </button>
                          </Can>
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Qty Disponible</span>
                      <p className="text-[var(--color-text)] font-bold text-lg">{((detail.qty_on_hand ?? 0) - (detail.qty_allocated ?? 0)).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-[var(--color-text-secondary)]">Costo Unitario</span>
                      {!canMoney ? (
                        <p className="text-[var(--color-text-muted)]">—</p>
                      ) : editingCost ? (
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editCostValue}
                            onChange={e => setEditCostValue(e.target.value)}
                            className="w-24 px-2 py-1 text-sm rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                            autoFocus
                          />
                          <button
                            onClick={handleUpdateCost}
                            disabled={!!actionLoading.updateCost}
                            className="text-xs px-2 py-1 rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50"
                          >
                            {actionLoading.updateCost ? <Loader2 size={12} className="animate-spin" /> : 'Guardar'}
                          </button>
                          <button
                            onClick={() => setEditingCost(false)}
                            className="text-xs px-2 py-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)]"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-[var(--color-text)] font-mono font-bold">{fmt(detail.unit_cost)}</span>
                          <Can cap="purchases.edit_cost">
                            <button
                              onClick={() => { setEditingCost(true); setEditCostValue(String(detail.unit_cost)); }}
                              className="p-0.5 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]"
                              title="Editar costo"
                            >
                              <Edit2 size={12} />
                            </button>
                          </Can>
                        </div>
                      )}
                    </div>
                    {detail.notes && (
                      <div className="col-span-2">
                        <span className="text-[var(--color-text-secondary)]">Notas</span>
                        <p className="text-[var(--color-text)] text-xs">{detail.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Linked Items */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">Items Vinculados (ML)</h4>
                    {detail.asin && canOperate && (
                      <button
                        onClick={() => loadSuggestions(detail.id)}
                        className="text-xs flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                      >
                        <Link2 size={12} />
                        Buscar items
                      </button>
                    )}
                  </div>

                  {detail.linked_items.length === 0 ? (
                    <p className="text-xs text-[var(--color-text-secondary)] py-2">No hay items vinculados.</p>
                  ) : (
                    <div className="space-y-2">
                      {detail.linked_items.map(li => (
                        <div
                          key={li.item_id}
                          className="flex items-center justify-between p-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-[var(--color-text)]">{li.item_id}</span>
                              {li.status && (
                                <StatusBadge
                                  status={li.status === 'active' ? 'ok' : li.status === 'paused' ? 'warning' : 'unknown'}
                                  label={li.status}
                                  size="sm"
                                />
                              )}
                            </div>
                            {li.title && <p className="text-xs text-[var(--color-text-secondary)] mt-0.5">{li.title}</p>}
                          </div>
                          <div className="flex items-center gap-1">
                            <a
                              href={`https://www.mercadolibre.com/item/${li.item_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]"
                              title="Ver en ML"
                            >
                              <ExternalLink size={12} />
                            </a>
                            <Can cap="purchases.operate_internal_stock">
                              <button
                                onClick={() => handleUnlink(detail.id, li.item_id)}
                                disabled={!!actionLoading[`unlink_${li.item_id}`]}
                                className="p-1 rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-error)] hover:text-[var(--color-error)]"
                                title="Desvincular"
                              >
                                {actionLoading[`unlink_${li.item_id}`] ? <Loader2 size={12} className="animate-spin" /> : <Unlink size={12} />}
                              </button>
                            </Can>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ML Suggestions Panel */}
                {showSuggestions && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">Sugerencias ML</h4>
                      <button onClick={() => setShowSuggestions(false)} className="text-xs text-[var(--color-text-secondary)] hover:underline">Cerrar</button>
                    </div>
                    {suggestionsLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 size={18} className="animate-spin text-[var(--color-primary)]" />
                        <span className="ml-2 text-xs text-[var(--color-text-secondary)]">Buscando items...</span>
                      </div>
                    ) : suggestions.length === 0 ? (
                      <p className="text-xs text-[var(--color-text-secondary)] py-2">No se encontraron sugerencias para este ASIN.</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {suggestions.map(s => (
                          <div
                            key={s.item_id}
                            className="flex items-center gap-3 p-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)]"
                          >
                            {s.thumbnail && (
                              <img src={s.thumbnail} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-mono text-[var(--color-text)]">{s.item_id}</span>
                                {s.site_id && <span className="text-xs text-[var(--color-text-secondary)]">{s.site_id}</span>}
                                <StatusBadge
                                  status={s.status === 'active' ? 'ok' : s.status === 'paused' ? 'warning' : 'unknown'}
                                  label={s.status}
                                  size="sm"
                                />
                              </div>
                              <p className="text-xs text-[var(--color-text-secondary)]">{s.title}</p>
                              {s.price != null && canMoney && <span className="text-xs font-mono text-[var(--color-text)]">{fmt(s.price)}</span>}
                            </div>
                            <Can cap="purchases.operate_internal_stock">
                              <button
                                onClick={() => handleLink(detail.id, s.item_id)}
                                disabled={linkingItemId === s.item_id}
                                className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                              >
                                {linkingItemId === s.item_id ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                                Vincular
                              </button>
                            </Can>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Movements */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">Movimientos</h4>
                  {detail.movements.length === 0 ? (
                    <p className="text-xs text-[var(--color-text-secondary)] py-2">Sin movimientos registrados.</p>
                  ) : (
                    <div className="space-y-1">
                      {detail.movements.map(m => (
                        <div key={m.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-[var(--color-bg)] text-xs border-b border-[var(--color-border)] last:border-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${m.qty > 0 ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'}`}>
                              {m.qty > 0 ? '+' : ''}{m.qty}
                            </span>
                            <span className="text-[var(--color-text)]">{MOVEMENT_LABELS[m.movement_type] || m.movement_type}</span>
                            {m.reference_id && (
                              <span className="text-[var(--color-text-secondary)] font-mono">#{m.reference_id}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-[var(--color-text-secondary)]">Saldo: {m.balance_after}</span>
                            <span className="text-[var(--color-text-secondary)]">{timeAgo(m.created_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                {detail.linked_item_id && (canReprice || canOperate) && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-[var(--color-text)] uppercase tracking-wider">Acciones</h4>
                    <div className="flex gap-2">
                      {canReprice && (
                        <button
                          onClick={() => handleReprice(detail.id)}
                          disabled={!!actionLoading[`reprice_${detail.id}`]}
                          className="flex items-center gap-2 px-3 py-2 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-50 transition-colors"
                        >
                          {actionLoading[`reprice_${detail.id}`] ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
                          Recalcular Precio
                        </button>
                      )}
                      {canOperate && (
                        <button
                          onClick={() => handleSyncML(detail.id)}
                          disabled={!!actionLoading[`sync_${detail.id}`]}
                          className="flex items-center gap-2 px-3 py-2 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-secondary)] disabled:opacity-50 transition-colors"
                        >
                          {actionLoading[`sync_${detail.id}`] ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                          Sync Stock ML
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Convert to Internal modal */}
      {convertTarget && (
        <ConvertToInternalModal
          isOpen={true}
          onClose={() => setConvertTarget(null)}
          onSuccess={() => {
            setConvertTarget(null);
            fetchRescatables();
            fetchProducts();
            fetchStats();
          }}
          sellerId={sellerId}
          stockProductId={convertTarget.stockProductId}
          productTitle={convertTarget.productTitle}
          productAsin={convertTarget.productAsin}
          qtyOnHand={convertTarget.qtyOnHand}
          defaultQty={convertTarget.defaultQty}
          defaultUnitCost={canMoney ? convertTarget.defaultUnitCost : 0}
          source={{ type: 'manual_inventory' }}
        />
      )}
    </div>
  );
}

